import { injectable } from 'inversify';
import { createSelector } from '@reduxjs/toolkit';
import {
  AssessmentRepository,
  IncidentRepository,
} from '../../../state/reduxRepositories';
import { AssessmentQueryParamService } from './assessment.queryParam.service';
import { useAppSelector } from '../../../state/hooks';
import { Incident } from '../../../state/reduxRepositories/incident/incident.interface';
import { LatestIncidentReport } from '../../../pages/incidentsList/incidentsList.interface';
import { Assessment } from '../../../state/reduxRepositories/assessments/assessments.interfaces';
import clinicAssessmentSpecs from './takeAssessment/md_portal_clearance_screen.json';

@injectable()
export class AssessmentDomainService {
  selectedIncidentByPlayerSelector: any;
  public constructor(
    private assessmentRepository: AssessmentRepository,
    private incidentRepository: IncidentRepository,
    private assessmentQueryParamService: AssessmentQueryParamService,
  ) {
    this.createSelectedIncidentByPlayerSelector();
  }

  useAssessmentResourceSelector = () => {
    return useAppSelector(
      this.assessmentRepository.selectors.resourcesSelector,
    );
  };

  useSelectedPlayerIncidentSelector = (): Incident => {
    return useAppSelector(this.selectedIncidentByPlayerSelector);
  };

  usePlayerAssessmentsSelector = (): Assessment[] => {
    return useAppSelector(
      createSelector(
        [
          this.assessmentQueryParamService.mergedQuerySelector,
          this.assessmentRepository.selectors.resourcesSelector,
        ],
        (queryParam, assessments) =>
          assessments
            .filter(
              (assessment) =>
                assessment.playerId === queryParam.selectedPlayerId &&
                assessment.assessment?.assessmentType ===
                  clinicAssessmentSpecs.info.identifier,
            )
            .sort((a: Assessment, b: Assessment) =>
              b?.createdAt?.localeCompare(a?.createdAt),
            ),
      ),
    );
  };

  private getIncidentMaximumDatePerPlayer = (
    incidents: any,
    playerId?: string,
  ) => {
    const filter = incidents.filter(
      (incident: Incident) => incident.playerId === playerId,
    );
    const maxDate = Math.max(
      ...filter.map((e: any) => new Date(e.incidentDateTime)),
    );
    return new Date(maxDate).getTime();
  };

  createSelectedIncidentByPlayerSelector = () => {
    this.selectedIncidentByPlayerSelector = createSelector(
      this.assessmentQueryParamService.mergedQuerySelector,
      this.incidentRepository.selectors.resourcesSelector,
      (queryParam: any, incidents: Incident[]): LatestIncidentReport => {
        const filterIncidents = incidents.find(
          (incident: Incident) =>
            incident.playerId === queryParam.selectedPlayerId &&
            new Date(incident.incidentDateTime).getTime() ===
              this.getIncidentMaximumDatePerPlayer(
                incidents,
                incident.playerId,
              ),
        );
        return {
          incidentDateTime: filterIncidents?.incidentDateTime,
          report: filterIncidents?.report,
        };
      },
    );
  };
}
